import React, { MouseEventHandler, useCallback, useEffect, useMemo, useState } from 'react';
import { DateRangeSelectProps } from './interfaces';
import { Input } from '../Input';
import { Dropdown, useDropdown } from '../Dropdown';
import { DoubleCalendar, OnDateRangeSelect } from '../Calendar';
import { ClearButton } from '../ClearButton';
import moment from 'moment';
import classNames from 'classnames';
import './DateRangeSelect.scss';

export const DateRangeSelect: React.FC<DateRangeSelectProps> = (props) => {
    const {
        currentValue,
        initialMount,
        initialYear,
        onDateRangeSelect,
        format,
        dropdownPlacement,
        className,
        wrapperClassName,
        inputWrapperClassName,
        allowClear = false,
        hideOnSelect = true,
        firstPlaceholder,
        secondPlaceholder,
        disabled = false,
    } = props;

    const { setDropdownShown, dropDownToggle, dropdownShown, dropdownOptions } = useDropdown();

    const [firstInputValue, setFirstInputValue] = useState<string>(
        currentValue?.[0] && format ? moment(currentValue[0]).format(format) : currentValue?.[0] || '',
    );
    const [secondInputValue, setSecondInputValue] = useState<string>(
        currentValue?.[1] && format ? moment(currentValue[1]).format(format) : currentValue?.[1] || '',
    );

    const onWrapperClick = useCallback<React.MouseEventHandler<HTMLDivElement>>(() => {
        if (!dropdownShown && !disabled) dropDownToggle();
    }, [dropDownToggle, dropdownShown, disabled]);

    const onSelectHandler = useCallback<OnDateRangeSelect>(
        (dates, moments) => {
            onDateRangeSelect(dates, moments);
        },
        [onDateRangeSelect],
    );

    const clearShowCondition = useMemo(() => {
        return (
            !disabled &&
            allowClear &&
            ((currentValue?.[0] !== null && currentValue?.[0] !== undefined) ||
                (currentValue?.[1] !== null && currentValue?.[1] !== undefined))
        );
    }, [currentValue, allowClear, disabled]);

    const onClear = useCallback<MouseEventHandler>(
        (event) => {
            event?.stopPropagation();
            if (clearShowCondition) onDateRangeSelect([null, null], [null, null]);
        },
        [onDateRangeSelect, clearShowCondition],
    );

    useEffect(() => {
        setFirstInputValue(
            currentValue?.[0] && format ? moment(currentValue[0]).format(format) : currentValue?.[0] || '',
        );
        setSecondInputValue(
            currentValue?.[1] && format ? moment(currentValue[1]).format(format) : currentValue?.[1] || '',
        );
        if (hideOnSelect && currentValue?.[1]) setDropdownShown(false);
    }, [currentValue, format, hideOnSelect, setDropdownShown]);

    return (
        <div
            className={classNames('ui-date-range-select', wrapperClassName)}
            onClick={onWrapperClick}
        >
            <Input
                className={classNames('ui-date-range-select__input', 'input-not-caret', className)}
                value={firstInputValue}
                wrapperClassName={classNames('ui-date-range-select__input-wrapper', 'left', inputWrapperClassName)}
                placeholder={firstPlaceholder}
                inputSize='large'
                disabled={disabled}
                onChange={() => {}}
            />
            <Input
                className={classNames('ui-date-range-select__input', 'input-not-caret', inputWrapperClassName)}
                value={secondInputValue}
                wrapperClassName={classNames('ui-date-range-select__input-wrapper', 'right', inputWrapperClassName)}
                placeholder={secondPlaceholder}
                inputSize='large'
                disabled={disabled}
                suffixIcon={clearShowCondition ? <ClearButton /> : <etna-icon name='Calendar' />}
                onSuffixIconClick={clearShowCondition ? onClear : undefined}
                onChange={() => {}}
            />
            <Dropdown
                dropdownPlacement={dropdownPlacement}
                dropdownOptions={dropdownOptions}
            >
                <DoubleCalendar
                    onDateRangeSelect={onSelectHandler}
                    currentValue={currentValue}
                    initialMount={initialMount}
                    initialYear={initialYear}
                />
            </Dropdown>
        </div>
    );
};
